import React, { useState, useEffect } from "react";
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Typography,
    Box,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { styled } from "@mui/material/styles";
import { ResourceLinks } from "../../../../constants";
import { makeArrayRandom } from "../../../../utils/learner";
import { floatOnly } from "../../../../utils";

const CustomAccordion = styled(Accordion)(({ theme }) => ({
    "&:before": {
        display: "none",
    },
    "&.MuiAccordion-root.Mui-expanded": {
        marginTop: 0,
        marginBottom: 0,
    },
}));

const CustomAccordionSummary = styled(AccordionSummary)(({ theme }) => ({
    backgroundColor: "rgb(217, 217, 217)",
    borderBottom: "none",
    "&.Mui-expanded": {
        backgroundColor: "rgb(0, 176, 240)",
        borderBottom: "10px solid rgb(0, 133, 180)",
    },
}));

export default function WebNumeracyInputWithMatch({ question, onAnswer = () => {} }) {
    const questionData = question;
    const imagesResources = ResourceLinks.llnQuizImage;

    const [expanded, setExpanded] = useState("panel1");
    const [answerBox, setAnswerBox] = useState(questionData.data.answerBox);
    const [answer, setAnswer] = useState(questionData.data.initialState);
    const [choices, setChoices] = useState({});
    const [selectedChoice, setSelectedChoice] = useState(null);
    const [selectedKeyPrevious, setSelectedKeyPrevious] = useState(null);
    const [availableChoices, setAvailableChoices] = useState(makeArrayRandom(answerBox[3].choices));

    // every time question change we will set to reset needed to reset
    useEffect(() => {
        setExpanded("panel1");
        setAnswerBox(questionData.data.answerBox);
        setAnswer(questionData.data.initialState);
        setChoices({});
        setSelectedChoice(null);
        setSelectedKeyPrevious(null);
        setAvailableChoices(makeArrayRandom(answerBox[3].choices));
    }, [question, 
        questionData.data.answerBox,
        questionData.data.initialState,
        answerBox,
    ]);

    const handleTableInput = (mainStateKey, 
        childStateKey, 
        answerType, 
        e, 
        index,
        childAnswerType,
        isFloatOnly,
    ) => {
        let processAnswer = isFloatOnly ? false : true;
        if (isFloatOnly && (floatOnly(e.target.value) || e.target.value === '')) {
            processAnswer = true;
        }

        if(processAnswer) {
            const value = e.target.value.trim();
            processTableAnswer(mainStateKey, 
                childStateKey, 
                answerType, 
                value, 
                index,
                childAnswerType
            );
        }
    };

    const processTableAnswer = (mainStateKey, 
        childStateKey, 
        answerType, 
        value, 
        index,
        childAnswerType) => {
        let ans = {...answer};

        if (answerType === "Array") {
            ans[mainStateKey][childStateKey][index]= value;
        } else { // check if String or Object

            if (childAnswerType === "String") {
                ans[mainStateKey][childStateKey] = value !== "" ? value : "";
            } else { // Object
                ans[mainStateKey][childStateKey][index] = value !== "" ? value : "";
            }
        }

        const newAnswer = { ...answer, ...ans };

        setAnswer(newAnswer);
        checkCanProceed(newAnswer);
    }

    const handleInputChange = (mainStateKey, childStateKey, e) => {
        if (floatOnly(e.target.value) || e.target.value === "") {
            const value = e.target.value;
            let ans = {...answer};
            ans[mainStateKey][childStateKey]= value;

            const newAnswer = { ...answer, ...ans };
            setAnswer(newAnswer);
            checkCanProceed(newAnswer);
        }
    }

    const handleMatchAnswer = (mainStateKey, childStateKey, lastKey, value) => {
        let ans = {...answer};
        ans[mainStateKey][childStateKey][lastKey] = value;
        setAnswer(ans);
        checkCanProceed(ans);
    }

    const handleChoiceClick = (choice) => {
        setSelectedChoice(choice);
        setSelectedKeyPrevious(null);
    };

    const handleChange = (panel) => (event, isExpanded) => {
        setExpanded(isExpanded ? panel : false);
    };

    const handleChoiceAssign = (index, mainStateKey, childStateKey, lastKey) => {
        if (choices[index] && !selectedChoice) {
            const choice = answerBox[3].choices.find((c) => c.value === choices[index].value);
            setSelectedChoice(choice);
            setSelectedKeyPrevious(index);
        }

        if (selectedChoice && (selectedChoice?.value !== choices[index]?.value)) {
            let currentChoices = {...choices};
            const previousChoice = choices[index];

            if (previousChoice) {
                setAvailableChoices((prevChoices) => [
                    ...prevChoices,
                    { ...previousChoice },
                ]);
            }

            // we will check here if coming from previous answer
            if (selectedKeyPrevious !== null) {
                delete currentChoices[selectedKeyPrevious];
                setSelectedKeyPrevious(null);
            }
            // updating answer
            currentChoices = {
                ...currentChoices,
                [index]: {
                    value: selectedChoice.value,
                },
            }
            setChoices(currentChoices);

            if (
                typeof questionData.answer.value === "object" &&
                !Array.isArray(questionData.answer.value)
            ) {
                
                handleMatchAnswer(mainStateKey, childStateKey, lastKey, selectedChoice.value);

            }

            setAvailableChoices((prevChoices) =>
                prevChoices.filter(
                    (item) => item.value !== selectedChoice.value
                )
            );

            setSelectedChoice(null);
        }
    };

    const handleRemoveChoice = (index, mainStateKey, childStateKey, lastKey) => {
        const removedChoice = choices[index];
        
        if (removedChoice) {
            setChoices((prevChoices) => {
                const newChoices = { ...prevChoices };
                delete newChoices[index];

                if (
                    typeof questionData.answer.value === "object" &&
                    !Array.isArray(questionData.answer.value)
                ) {
                    handleMatchAnswer(mainStateKey, childStateKey, lastKey, "");

                } else {
                }

                return newChoices;
            });

            setAvailableChoices((prevChoices) => [
                ...prevChoices,
                {
                    value: removedChoice.value,
                },
            ]);
        }
    };

    const checkCanProceed = (ans) => {
        let canProceed = false;
        const answerKeys = Object.keys(ans);
        answerKeys.map((keys) => {
            if (keys === "Formulas") {
                const value = ans[keys];
                const valueKeys = Object.keys(value);
                valueKeys.map((fKeys) => {
                    const childValue = value[fKeys];
                    if (typeof childValue === "object") {
                        const formulaKeys = Object.keys(childValue);
                        formulaKeys.map((finalKey) => {
                            if (childValue[finalKey] !== "" && childValue[finalKey] !== null) {
                                canProceed = true;
                            }
                            return null;
                        });
                    }
                    return null;
                });
            } else if (keys === "Metric Units") {
                const value = ans[keys];
                const valueKeys = Object.keys(value);
                valueKeys.map((fKeys) => {
                    const childValue = value[fKeys];
                    if (typeof childValue === "object") {
                        const formulaKeys = Object.keys(childValue);
                        formulaKeys.map((finalKey) => {
                            if (childValue[finalKey] !== "" && childValue[finalKey] !== null) {
                                canProceed = true;
                            }
                            return null;
                        });
                    } else if ((typeof childValue === "number" || typeof childValue === "string") && childValue!== "" && childValue !== null) {
                        canProceed = true;
                    }
                    return null;
                });
                
            } else if (keys === "Ratios and Powers") {
                const value = ans[keys];
                const valueKeys = Object.keys(value);
                valueKeys.map((fKeys) => {
                    const childValue = value[fKeys];
                    if (childValue !== "" && childValue !== null) {
                        canProceed = true;
                    }
                    return null;
                });
            } else if (keys === "Fractions, Percentages & Decimals") {
                const value = ans[keys];
                const valueKeys = Object.keys(value);
                valueKeys.map((fKeys) => {
                    const childValue = value[fKeys];
                    childValue.map((child, keys) => {
                        if (child !== "" && child !== null) {
                            canProceed = true;
                        }
                        return null;
                    });
                    return null;
                });
            }

            return null;
        });
        
        onAnswer({
            canProceed,
            answer: ans,
        });
    }

    return (
        <Box sx={{ maxWidth: 1100, margin: "0 auto", padding: "20px" }}>
            {/* Scenario Accordion */}
            {answerBox.map((row, index) => {
                return (
                    <React.Fragment key={index}>
                    {row.type === "table" && (
                        <CustomAccordion
                            expanded={expanded === row.panelId}
                            onChange={handleChange(row.panelId)}
                        >
                            <CustomAccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                aria-controls="scenario-content"
                                id="scenario-header"
                            >
                                <Typography sx={{ fontSize: "20px", fontWeight: "700" }}>
                                    {row.title}
                                </Typography>
                            </CustomAccordionSummary>
                            <AccordionDetails>
                                <Box
                                    display="flex"
                                    flexDirection="row"
                                    flexWrap="wrap"
                                    alignItems="left"
                                    justifyContent="space-around"
                                    borderTop="10px solid rgb(191, 191, 191)"
                                    sx={{
                                        textAlign: "left",
                                    }}
                                >
                                    <Box sx={{
                                        display: "flex",
                                        justifyContent: "space-around",
                                        height: "350px",
                                        fontSize: "22px",
                                        textAlign: "center",
                                        marginTop: "10px",
                                    }}>
                                        {/* For Question */}
                                        <p style={{
                                            fontWeight: "bold",
                                            width: "200px",
                                            fontSize: "22px",
                                            textAlign: "center",
                                        }}>{row.question}</p>

                                        {/* For Table */}
                                        <table style={{
                                            width: "700px",
                                            height: "250px",
                                            textAlign: "center",
                                            borderCollapse: "collapse",
                                            fontSize: "22px",
                                        }}>
                                            <thead style={{
                                                textAlign: "center",
                                                fontWeight: "bold",
                                                backgroundColor: row.tableInfo.headerColor,
                                                fontSize: "22px",
                                            }}>
                                                <tr>
                                                    {row.tableInfo.header.map((value, headerIndex) => (
                                                        <th key={headerIndex} style={{
                                                            border: "1px solid black",
                                                            textAlign: "center",
                                                            fontWeight: "bold",
                                                        }}>{value}</th>
                                                    ))}
                                                </tr>
                                            </thead>
                                            <tbody sx={{
                                                fontSize: "30px",
                                                textAlign: "center",
                                            }}>
                                                {/* For Question Body */}
                                                {row.tableInfo.body.map((bodyRow, bodyIndex) => (
                                                    <tr key={bodyIndex} sx={{
                                                        fontSize: "30px",
                                                        textAlign: "center",
                                                    }}> 
                                                        {bodyRow.map((bodyRowData, bodyRowDataIndex) => (
                                                            <td key={bodyRowDataIndex}
                                                                style={{
                                                                    border: "1px solid black",
                                                                    fontSize: "30px",
                                                                    textAlign: "center"
                                                                }}
                                                            >
                                                                {bodyRowData.isEditable 
                                                                ? 
                                                                    <Box sx={{
                                                                        display: "inline-flex",
                                                                        width: "150px",
                                                                        fontSize: "16px",
                                                                        position: "relative",
                                                                        textAlign: "center",
                                                                    }}>
                                                                        <input type="text" 
                                                                            value={
                                                                                (row.answerType === "Array" 
                                                                                    ? answer[row.stateKey][bodyRowData.stateKey][bodyRowData.index]
                                                                                    : (
                                                                                        bodyRowData.answerType === "String"
                                                                                            ? answer[row.stateKey][bodyRowData.stateKey]
                                                                                            : answer[row.stateKey][bodyRowData.stateKey][bodyRowData.index]
                                                                                    )
                                                                                )
                                                                            }
                                                                            style={{
                                                                                height: "100%",
                                                                                padding: "2px",
                                                                                boxSizing: "border-box",
                                                                                width: "100%",
                                                                                outline: "none",
                                                                                border: "none",
                                                                                borderBottom: "2px solid #888888",
                                                                                fontSize: "22px",
                                                                                textAlign: "center",
                                                                                marginLeft: "2px",
                                                                            }}
                                                                            onChange={(event) => 
                                                                                handleTableInput(row.stateKey, 
                                                                                    bodyRowData.stateKey, 
                                                                                    row.answerType, 
                                                                                    event,
                                                                                    bodyRowData.index,
                                                                                    bodyRowData.answerType,
                                                                                    bodyRowData.isFloatOnly
                                                                                )}
                                                                        />
                                                                    </Box>
                                                                    : 
                                                                    bodyRowData.value
                                                                }
                                                            </td>
                                                        ))}
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </Box>

                                </Box>
                            </AccordionDetails>
                        </CustomAccordion>
                    )}
                    {row.type === "input" && (
                        <CustomAccordion
                            expanded={expanded === row.panelId}
                            onChange={handleChange(row.panelId)}
                        >
                            <CustomAccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                aria-controls="scenario-content"
                                id="scenario-header"
                            >
                                <Typography sx={{ fontSize: "20px", fontWeight: "700" }}>
                                    {row.title}
                                </Typography>
                            </CustomAccordionSummary>
                            <AccordionDetails>
                                <Box
                                    display="flex"
                                    flexDirection="row"
                                    flexWrap="wrap"
                                    alignItems="left"
                                    justifyContent="space-around"
                                    borderTop="10px solid rgb(191, 191, 191)"
                                    sx={{
                                        textAlign: "left",
                                    }}
                                >
                                    <Box sx={{
                                        height: "350px",
                                        fontSize: "28px",
                                        textAlign: "center",
                                        marginTop: "10px",
                                    }}>
                                        {row.inputs.map((input, inputIndex) => (
                                            <Box key={inputIndex} 
                                                sx={{
                                                    display: "inline-block",
                                                    height: "33%",
                                                    width: "50%",
                                                    fontWeight: "bold",
                                                    fontSize: "28px",
                                                    textAlign: "center",
                                                }}
                                            >
                                                <span>{input.value}</span>
                                                <Box sx={{
                                                    display: "inline-block",
                                                    height: "50px",
                                                    width: "60px",
                                                    fontSize: "18px",
                                                    position: "relative",
                                                    fontWeight: "bold",
                                                }}>
                                                    <input type="text"
                                                        value={answer[row.stateKey][input.stateKey]}
                                                        style={{
                                                            boxSizing: "border-box",
                                                            width: "100%",
                                                            height: "2em",
                                                            outline: "none",
                                                            border: "none",
                                                            borderBottom: "2px solid #888888",
                                                            fontSize: "1.5em",
                                                            fontWeight: "bold",
                                                            textAlign: "center",
                                                        }}    
                                                        onChange={(e) => handleInputChange(row.stateKey, input.stateKey, e)}
                                                    />
                                                </Box>
                                            </Box>
                                        ))}
                                    </Box>
                                </Box>
                            </AccordionDetails>
                        </CustomAccordion>
                    )}

                    {row.type === "match" && (
                        <CustomAccordion
                        expanded={expanded === row.panelId}
                        onChange={handleChange(row.panelId)}
                    >
                        <CustomAccordionSummary
                            expandIcon={<ExpandMoreIcon />}
                            aria-controls="scenario-content"
                            id="scenario-header"
                        >
                            <Typography sx={{ fontSize: "20px", fontWeight: "700" }}>
                                {row.title}
                            </Typography>
                        </CustomAccordionSummary>
                        <AccordionDetails>
                            <Box
                                display="flex"
                                flexDirection="row"
                                flexWrap="wrap"
                                alignItems="left"
                                justifyContent="space-around"
                                borderTop="10px solid rgb(191, 191, 191)"
                                sx={{
                                    textAlign: "left",
                                }}
                            >
                                <Box sx={{
                                    display: "flex",
                                    height: "270px",
                                    position: "relative",
                                    fontSize: "22px",
                                    textAlign: "center",
                                    marginTop: "10px",
                                }}>
                                    {/* For Answer */}
                                    <Box sx={{
                                        flexGrow: "1",
                                        fontSize: "22px",
                                        textAlign: "center",
                                        width: "750px",
                                    }}>
                                        <Box sx={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "center",
                                            marginLeft: "15px",
                                            marginRight: "15px",
                                            fontSize: "22px",
                                            textAlign: "center",
                                        }}>
                                            {row.answerBox.map((answer, answerIndex) => (
                                                <Box component="img" 
                                                    key={answerIndex}
                                                    src={`${imagesResources}${answer.image}`}
                                                    sx={{
                                                        textAlign: "center",
                                                    }}
                                                />
                                            ))}
                                        </Box>

                                        <Box sx={{
                                            display: "flex",
                                            justifyContent: "space-between",
                                            alignItems: "flex-start",
                                            marginTop: "10px",
                                            fontSize: "22px",
                                            textAlign: "center",
                                        }}>
                                            {row.answerBox.map((answer, answerIndex) => (
                                                <Box key={answerIndex}
                                                    sx={{
                                                        width: "110px",
                                                        textAlign: "left",
                                                        fontSize: "22px",
                                                    }}
                                                >
                                                    {answer.children.map((answerChild, answerChildIndex) => (
                                                        <React.Fragment key={answerChildIndex}>
                                                            <span>{answerChild.value}</span>
                                                            <Box sx={{
                                                                height: "30px",
                                                                width: "110px",
                                                                position: "relative",
                                                                display: "inline-block",
                                                                boxSizing: "border-box",
                                                                padding: "4px",
                                                                lineHeight: "0.8em",
                                                                textAlign: "left",
                                                                fontSize: "22px",
                                                                boxShadow:
                                                                selectedChoice
                                                                        ? "rgb(199, 0, 199) 0px 0px 0px 3px"
                                                                        : "none",
                                                                backgroundColor: choices[answerChild.choiceKey]
                                                                        ? "#aad6e7"
                                                                        : "rgb(230, 230, 230);",
                                                                borderTop: choices[answerChild.choiceKey]
                                                                        ? "none"
                                                                        : "10px solid rgb(91, 105, 106)",
                                                                }}
                                                                onClick={() => handleChoiceAssign(
                                                                    answerChild.choiceKey, 
                                                                    row.stateKey, 
                                                                    answer.stateKey, 
                                                                    answerChild.stateKey
                                                                )}
                                                            >
                                                                {choices[answerChild.choiceKey] ? (
                                                                    <Box
                                                                        sx={{
                                                                            top: "initial",
                                                                            position: "absolute",
                                                                            bottom: 0,
                                                                            left: 0,
                                                                            right: 0,
                                                                            lineHeight: "0.8em",
                                                                            fontWeight: "bold",
                                                                            fontSize: "55px",
                                                                            textAlign: "center"
                                                                        }}
                                                                    >
                                                                        <Box sx={{
                                                                            position: "relative",
                                                                            lineHeight: "1em",
                                                                            fontWeight: "bold",
                                                                            fontSize: "20px",
                                                                            height: "22px",
                                                                            width: "100px",
                                                                            display: "inline-block",
                                                                            boxSizing: "content-box",
                                                                            backgroundColor: "#aad6e7",
                                                                            padding: "4px",
                                                                            textAlign: "center",
                                                                        }}>
                                                                            <Box sx={{
                                                                                display: "inline-flex",
                                                                                width: "100%",
                                                                                height: "100%",
                                                                                alignItems: "center",
                                                                                justifyContent: "center",
                                                                                userSelect: "none",
                                                                                pointerEvents: "none",
                                                                                fontSize: "16px",
                                                                                lineHeight: "1em",
                                                                                fontWeight: "bold",
                                                                                textAlign: "center"
                                                                            }}>{choices[answerChild.choiceKey].value}</Box>
                                                                        </Box>
                                                                        {/* Close  */}
                                                                        <Box
                                                                            className="selected-close-btn"
                                                                            sx={{
                                                                                right: "0px",
                                                                                top: "20px",
                                                                                padding: "4px",
                                                                                display: "content-box",
                                                                                lineHeight: "0.8em",
                                                                                fontWeight: "bold",
                                                                                fontSize: "16px",
                                                                            }}
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                handleRemoveChoice(
                                                                                    answerChild.choiceKey, 
                                                                                    row.stateKey, 
                                                                                    answer.stateKey, 
                                                                                    answerChild.stateKey
                                                                                );
                                                                            }}
                                                                        >X</Box>
                                                                    </Box>
                                                                        ) :
                                                                        <span></span>
                                                                    }
                                                            </Box>
                                                        </React.Fragment>
                                                    ))}
                                                </Box>
                                            ))}
                                        </Box>
                                    </Box>

                                    {/* For Choices */}
                                    <Box sx={{
                                        position: "relative",
                                        width: "140px",
                                        marginLeft: "10px",
                                        border: "1px dashed lightgrey",
                                        fontSize: "22px",
                                        textAlign: "center",
                                    }}>
                                        <Box sx={{
                                            position: "absolute",
                                            top: "0",
                                            left: "0",
                                            fontSize: "22px",
                                            textAlign: "center",
                                        }}>
                                            {availableChoices.map((choice, choiceIndex) => {
                                                return (
                                                    <Box key={choiceIndex}
                                                        sx={{
                                                            borderBottom: "10px solid rgb(52, 148, 186)",
                                                            position: "relative",
                                                            height: "22px",
                                                            width: "102px",
                                                            display: "inline-block",
                                                            boxSizing: "content-box",
                                                            backgroundColor: "#aad6e7",
                                                            padding: "4px",
                                                            lineHeight: "0.8em",
                                                            fontSize: "22px",
                                                            textAlign: "center",
                                                            boxShadow:
                                                            selectedChoice?.value === choice.value
                                                                ? "rgb(199, 0, 199) 0px 0px 0px 3px"
                                                                : "none",
                                                        }}
                                                        onClick={() => { handleChoiceClick(choice) }}
                                                        disabled={selectedChoice === choice}
                                                    >   
                                                        <Box sx={{
                                                            fontWeight: "bold",
                                                            display: "inline-flex",
                                                            width: "100%",
                                                            height: "100%",
                                                            alignItems: "center",
                                                            justifyContent: "center",
                                                            userSelect: "none",
                                                            pointerEvents: "none",
                                                            fontSize: "16px",
                                                            lineHeight: "0.8em",
                                                            textAlign: "center",
                                                        }}>{choice.value}</Box>
                                                    </Box>
                                                )
                                            })}
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>
                        </AccordionDetails>
                    </CustomAccordion>
                    )}
                    </React.Fragment>
                )
            })}
        </Box>
    );
};